<template>
  <button class="webauthn-login proceed" @click="login" :disabled="isLoggingIn">
    <template v-if="isLoggingIn">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;&nbsp;
    </template>
    <span>{{ isLoggingIn ? 'Logging in...' : 'Login with Device' }}</span>
  </button>
  <div v-if="error" class="error">{{ error }}</div>
  <div v-if="success" class="success">{{ success }}</div>
</template>

<script>
import { startAuthentication } from '@simplewebauthn/browser';
import api from '@/services/api';
import apiProxy from '@/services/apiProxy';
import { normalizeBrandId } from '@/modules/brandId';

export default {
  name: 'WebAuthnLogin',
  props: {
    brand_id: undefined,
    username: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoggingIn: false,
      error: '',
      success: ''
    };
  },
  methods: {
    async login() {
      if (!this.username || !this.username.trim()) {
        this.error = 'Email address is required';
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.username)) {
        this.error = 'Please enter a valid email address';
        return;
      }
      
      this.isLoggingIn = true;
      this.error = '';
      this.success = '';

      try {
        // Get authentication options from server
        const apiBrandId = normalizeBrandId(this.brand_id);
        const optionsResponse = await api.getAuthenticationOptions(this.username, apiBrandId);

        // Filter transports to prioritize platform authenticators
        const filteredOptions = {
          ...optionsResponse,
          allowCredentials: optionsResponse.allowCredentials.map(cred => ({
            ...cred,
            transports: cred.transports.filter(t => t === 'internal')
          }))
        };

        // Start authentication process in browser
        const assertionResponse = await startAuthentication(filteredOptions);

        // Send response to server for verification
        const verificationResponse = await api.verifyAuthentication(this.username, assertionResponse, apiBrandId);

        if (verificationResponse.verified) {
          this.success = 'Login successful!';
          
          // Store opaque session token for PCI compliance
          if (verificationResponse.session_token) {
            document.cookie = `session_id=${verificationResponse.session_token}; Path=/; Max-Age=3600; SameSite=Lax`;
            apiProxy.currentSessionToken = verificationResponse.session_token;
            this.$emit('fetch-user-details');
          }
          
          this.$emit('login-complete', verificationResponse.session_token);
        } else {
          this.error = 'Login failed. Please try again.';
          this.$emit('login-error', this.error);
        }
      } catch (error) {
        console.error(error);
        
        // Extract error message with priority for rate limiting and account lockout
        let errorMessage = 'Login failed';
        
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || error.response.data.error || errorMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.error = errorMessage;
        this.$emit('login-error', this.error);
      } finally {
        this.isLoggingIn = false;
      }
    },
    
    async handleUserLogin(uuid) {
      try {
        // Use the fetchUserDetails function from parent component
        this.$emit('fetch-user-details', uuid);
      } catch (error) {
        console.error('Error handling user login:', error);
      }
    }
  }
};
</script>

<style scoped>
.error {
  color: red;
  margin-bottom: 10px;
  width: 100%;
}
.success {
  color: green;
  margin-bottom: 10px;
  width: 100%;
}
button {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 4px;
}
button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

button.proceed {
  position: relative;
  width: 100%;
  flex: 1;
  padding: 1em 2rem;
  display: block;
  font-family: var(--details_buttons_loginmodal_font_family, inherit);
  font-size: var(--details_buttons_loginmodal_font_size, 1rem);
  font-weight: var(--details_buttons_loginmodal_font_weight, inherit);
  background: var(--details_buttons_loginmodal_background, #034a94);
  color: var(--details_buttons_loginmodal_font_color, #fff);
  border: var(--details_buttons_loginmodal_border, none);
  border-radius: var(--details_buttons_loginmodal_border_radius, 0.25rem);
}
</style>
